import {
  GoogleAuthProvider,
  PhoneMultiFactorInfo,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
  getMultiFactorResolver,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
} from "firebase/auth"
import React, { useEffect, useRef, useState } from "react"

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import { LoadingButton } from "@mui/lab"
import {
  EmailOutlined,
  Google,
  LockOutlined,
  Logout,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material"
import { Logo } from "./Logo"
import { UserDetail } from "./Users/UserDetail"
import { auth } from "../firebase"
import { colors } from "./theme"
import { getData } from "./core/CreateEditOptions/getData"
import { logout } from "./Users/Main"
import { useAuthState } from "react-firebase-hooks/auth"
import { LoadingService } from "react-ui-components/components/LoadingService"
import { PopupService } from "react-ui-components/components/PopupService"

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const AUTH_ERROR_MESSAGES: Record<string, string | null> = {
  "auth/invalid-email": "Enter a valid email address.",
  "auth/user-not-found": "No admin account found with this email.",
  "auth/wrong-password": "Incorrect password. Please try again.",
  "auth/user-disabled": "This account has been disabled. Contact support.",
  "auth/too-many-requests": "Too many attempts. Try again later.",
  "auth/network-request-failed": "Network error. Check your connection and try again.",
  "auth/popup-closed-by-user": null,
  "auth/popup-blocked": "Popup blocked. Allow popups and try again.",
  "auth/account-exists-with-different-credential":
    "Use the sign-in method linked to this email.",
}

const getAuthErrorMessage = (error: any) => {
  if (!error?.code) {
    return "Unable to sign in. Please try again."
  }

  const message = AUTH_ERROR_MESSAGES[error.code]
  if (message === null) {
    return null
  }

  return message || "Unable to sign in. Please try again."
}

export const googleProvider = new GoogleAuthProvider()
googleProvider.setCustomParameters({ prompt: "select_account" })

onAuthStateChanged(auth, async (user) => {
  if (user) {
    const idTokenResult = await user.getIdTokenResult()
    if (!idTokenResult.claims.Admin) {
      signOut(auth)
      PopupService.pushPopup("Access denied. Only admins can log in.", "Access denied")
    }
  }
})

type LoginMainProps = {
  children: React.ReactNode
}

const LoginMain = ({ children }: LoginMainProps) => {
  const [user, loading] = useAuthState(auth)
  const [selectedRowData, setSelectedRowData] = useState<any>(null)

  useEffect(() => {
    if (loading) {
      return
    }

    if (user && !user.isAnonymous) {
      getData({
        setData: (data: any) => {
          setSelectedRowData(data)
        },
        path: `users/${user.uid}`,
      })
    } else {
      setSelectedRowData(null)
    }
  }, [user, loading])

  if (loading) {
    return (
      <Box
        sx={{ minHeight: "40vh", display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <CircularProgress />
      </Box>
    )
  }

  if (user && !user.isAnonymous) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Button
          variant="outlined"
          startIcon={<Logout />}
          onClick={logout}
          sx={{ alignSelf: "flex-start" }}
        >
          Logout
        </Button>
        <UserDetail
          selectedRowData={selectedRowData}
          setLoading={LoadingService.setLoading}
          setSelectedRowData={setSelectedRowData}
        />
      </Box>
    )
  }

  return <>{children}</>
}

type MfaChallenge = {
  resolver: any
  verificationId: string
  phoneNumber: string
}

const Login = () => {
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isGoogleLoading, setIsGoogleLoading] = useState(false)
  const [formErrors, setFormErrors] = useState({ email: "", password: "" })
  const [authError, setAuthError] = useState<string | null>(null)

  const [mfaChallenge, setMfaChallenge] = useState<MfaChallenge | null>(null)
  const [mfaCode, setMfaCode] = useState("")
  const [mfaError, setMfaError] = useState<string | null>(null)
  const [isMfaSubmitting, setIsMfaSubmitting] = useState(false)

  const recaptchaVerifierRef = useRef<RecaptchaVerifier | null>(null)

  const isBusy = isSubmitting || isGoogleLoading || isMfaSubmitting
  const isFormDisabled = isBusy || !!mfaChallenge

  const validateForm = () => {
    const trimmedEmail = email.trim()
    const nextErrors = { email: "", password: "" }

    if (!trimmedEmail) {
      nextErrors.email = "Email is required."
    } else if (!emailRegex.test(trimmedEmail)) {
      nextErrors.email = "Enter a valid email address."
    }

    if (!password) {
      nextErrors.password = "Password is required."
    }

    setFormErrors(nextErrors)

    return !nextErrors.email && !nextErrors.password
  }

  const ensureRecaptcha = () => {
    if (!recaptchaVerifierRef.current) {
      recaptchaVerifierRef.current = new RecaptchaVerifier(
        auth,
        "mfa-recaptcha",
        {
          size: "invisible",
        },
      )
    }

    return recaptchaVerifierRef.current
  }

  const startMfaFlow = async (error: any) => {
    try {
      const resolver = getMultiFactorResolver(auth, error)
      const hint = resolver.hints[0]

      if (!hint) {
        setAuthError("Multi-factor authentication is required but no second factor is available.")
        return
      }

      if (hint.factorId !== PhoneMultiFactorGenerator.FACTOR_ID) {
        setAuthError("Unsupported multi-factor method.")
        return
      }

      const phoneAuthProvider = new PhoneAuthProvider(auth)
      const verificationId = await phoneAuthProvider.verifyPhoneNumber(
        {
          multiFactorHint: hint,
          session: resolver.session,
        },
        ensureRecaptcha(),
      )

      setMfaChallenge({
        resolver,
        verificationId,
        phoneNumber: (hint as PhoneMultiFactorInfo).phoneNumber || "your phone",
      })
      setMfaCode("")
      setMfaError(null)
    } catch (error: any) {
      recaptchaVerifierRef.current?.clear()
      recaptchaVerifierRef.current = null
      setAuthError("Unable to start multi-factor verification. Please try again.")
    }
  }

  const handleEmailLogin = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setAuthError(null)

    if (!validateForm()) {
      return
    }

    setIsSubmitting(true)
    try {
      await signInWithEmailAndPassword(auth, email.trim(), password)
    } catch (error: any) {
      if (error?.code === "auth/multi-factor-auth-required") {
        await startMfaFlow(error)
        return
      }

      const message = getAuthErrorMessage(error)
      if (message) {
        setAuthError(message)
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleGoogleLogin = async () => {
    setAuthError(null)
    setIsGoogleLoading(true)

    try {
      await signInWithPopup(auth, googleProvider)
    } catch (error: any) {
      const message = getAuthErrorMessage(error)
      if (message) {
        setAuthError(message)
      }
    } finally {
      setIsGoogleLoading(false)
    }
  }

  const handleMfaClose = () => {
    setMfaChallenge(null)
    setMfaCode("")
    setMfaError(null)
    recaptchaVerifierRef.current?.clear()
    recaptchaVerifierRef.current = null
  }

  const handleMfaVerify = async (event?: React.SyntheticEvent) => {
    event?.preventDefault()
    if (!mfaChallenge) {
      return
    }

    const trimmedCode = mfaCode.trim()
    if (!trimmedCode) {
      setMfaError("Enter the verification code.")
      return
    }

    setIsMfaSubmitting(true)
    try {
      const cred = PhoneAuthProvider.credential(mfaChallenge.verificationId, trimmedCode)
      const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred)
      await mfaChallenge.resolver.resolveSignIn(multiFactorAssertion)
      handleMfaClose()
    } catch {
      setMfaError("Invalid verification code. Please try again.")
    } finally {
      setIsMfaSubmitting(false)
    }
  }

  return (
    <LoginMain>
      <Box
        sx={{
          position: "relative",
          minHeight: { xs: "auto", md: "70vh" },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: { xs: 3, md: 5 },
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            borderRadius: 4,
            background: `radial-gradient(1200px 500px at 0% 0%, ${colors.palette.secondary100} 0%, ${colors.palette.neutral200} 50%, ${colors.palette.neutral100} 100%)`,
            opacity: 0.7,
          }}
        />
        <Paper
          elevation={8}
          sx={{
            position: "relative",
            zIndex: 1,
            width: "100%",
            maxWidth: 980,
            overflow: "hidden",
            borderRadius: 4,
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1.2fr 0.8fr" },
          }}
        >
          <Box sx={{ p: { xs: 3, md: 5 }, backgroundColor: colors.palette.neutral100 }}>
            <Stack spacing={3}>
              <Stack spacing={1}>
                <Logo width={140} />
                <Typography variant="h5" fontWeight={700} color={colors.text}>
                  Admin Sign In
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Use your administrator credentials to continue.
                </Typography>
              </Stack>

              {authError ? <Alert severity="error">{authError}</Alert> : null}

              <Box component="form" onSubmit={handleEmailLogin} noValidate>
                <Stack spacing={2}>
                  <TextField
                    autoFocus
                    fullWidth
                    label="Email"
                    type="email"
                    value={email}
                    onChange={(event) => {
                      setEmail(event.target.value)
                      if (formErrors.email) {
                        setFormErrors((prev) => ({ ...prev, email: "" }))
                      }
                      if (authError) {
                        setAuthError(null)
                      }
                    }}
                    error={!!formErrors.email}
                    helperText={formErrors.email}
                    disabled={isFormDisabled}
                    autoComplete="email"
                    inputProps={{ autoCapitalize: "none" }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailOutlined color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(event) => {
                      setPassword(event.target.value)
                      if (formErrors.password) {
                        setFormErrors((prev) => ({ ...prev, password: "" }))
                      }
                      if (authError) {
                        setAuthError(null)
                      }
                    }}
                    error={!!formErrors.password}
                    helperText={formErrors.password}
                    disabled={isFormDisabled}
                    autoComplete="current-password"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlined color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label={showPassword ? "Hide password" : "Show password"}
                            onClick={() => setShowPassword((prev) => !prev)}
                            edge="end"
                            disabled={isFormDisabled}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <LoadingButton
                    type="submit"
                    variant="contained"
                    size="large"
                    loading={isSubmitting}
                    disabled={isFormDisabled}
                    sx={{ py: 1.2 }}
                  >
                    Sign In
                  </LoadingButton>
                </Stack>
              </Box>

              <Divider>or</Divider>

              <LoadingButton
                variant="outlined"
                size="large"
                loading={isGoogleLoading}
                startIcon={<Google />}
                loadingPosition="start"
                onClick={handleGoogleLogin}
                disabled={isFormDisabled}
                sx={{ py: 1.1 }}
              >
                Continue with Google
              </LoadingButton>

              <Typography variant="caption" color="text.secondary">
                Access is restricted to approved admins. Contact your supervisor if you need access.
              </Typography>
            </Stack>
          </Box>

          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              flexDirection: "column",
              justifyContent: "space-between",
              p: 4,
              color: colors.palette.neutral100,
              background: `linear-gradient(160deg, ${colors.palette.primary500} 0%, ${colors.palette.primary300} 45%, ${colors.palette.secondary200} 100%)`,
            }}
          >
            <Stack spacing={2}>
              <Typography variant="h6" fontWeight={700}>
                WinFacil Admin
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Manage raffles, sponsors, notifications, and featured content from one secure hub.
              </Typography>
              <Stack spacing={1} sx={{ mt: 2 }}>
                <Typography variant="caption" sx={{ opacity: 0.85 }}>
                  - Real-time user insights
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.85 }}>
                  - Centralized raffle control
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.85 }}>
                  - Content publishing tools
                </Typography>
              </Stack>
            </Stack>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: colors.palette.accent400,
                }}
              />
              <Typography variant="caption" sx={{ opacity: 0.85 }}>
                Protected by multi-factor authentication
              </Typography>
            </Box>
          </Box>
        </Paper>
        <Box id="mfa-recaptcha" sx={{ position: "absolute", left: -9999, top: -9999 }} />
      </Box>

      <Dialog open={!!mfaChallenge} onClose={handleMfaClose} fullWidth maxWidth="xs">
        <DialogTitle>Verification required</DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleMfaVerify}>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Enter the verification code sent to {mfaChallenge?.phoneNumber || "your phone"}.
              </Typography>
              {mfaError ? <Alert severity="error">{mfaError}</Alert> : null}
              <TextField
                autoFocus
                fullWidth
                label="Verification code"
                value={mfaCode}
                onChange={(event) => {
                  setMfaCode(event.target.value)
                  if (mfaError) {
                    setMfaError(null)
                  }
                }}
                disabled={isMfaSubmitting}
                inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
              />
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleMfaClose} disabled={isMfaSubmitting}>
            Cancel
          </Button>
          <LoadingButton onClick={handleMfaVerify} loading={isMfaSubmitting} variant="contained">
            Verify
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </LoginMain>
  )
}

export default Login
